
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const CallForKnowledgeBounty = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [selectedSubdomain, setSelectedSubdomain] = useState<string>("");
  const [isBounty, setIsBounty] = useState<string | null>(null);

  // Extract the selected domain and subdomain from location state
  useEffect(() => {
    if (location.state) {
      setSelectedDomain(location.state.selectedDomain || "");
      setSelectedSubdomain(location.state.selectedSubdomain || "");
      
      // Auto-populate for Chevron demonstration - CfK gets $2k bounty
      if (location.state.chevronCall) {
        setIsBounty("yes"); // CfK gets a bounty
      }
    }
  }, [location]);

  const handleContinue = () => {
    navigate("/call-for-knowledge/curator", { 
      state: { 
        selectedDomain,
        selectedSubdomain,
        isBounty
      } 
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      {/* Logo */}
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/engage")}
        />
      </div>

      <Card className="w-full max-w-2xl border border-gray-200">
        <CardContent className="pt-6 px-6 pb-8">
          <div className="mb-4">
            <div className="text-sm text-gray-500 mb-1">Selected Knowledge Domain:</div>
            <div className="font-semibold text-lg">{selectedDomain}</div>
            {selectedSubdomain && (
              <>
                <div className="text-sm text-gray-500 mb-1 mt-2">Selected Subdomain:</div>
                <div className="font-semibold">{selectedSubdomain}</div>
              </>
            )}
          </div>

          <h2 className="text-2xl font-semibold mb-4">Would you like to create a knowledge bounty?</h2>
          
          <div className="mb-6">
            <p className="text-gray-600">
              A knowledge bounty rewards community members who contribute valuable information to fill identified knowledge gaps.
            </p>
          </div>

          <RadioGroup 
            value={isBounty || ""} 
            onValueChange={setIsBounty}
            className="space-y-4 mb-8"
          >
            <div className="flex items-start space-x-2 p-3 border rounded-md hover:bg-gray-50">
              <RadioGroupItem value="yes" id="bounty-yes" className="mt-1" />
              <div>
                <Label htmlFor="bounty-yes" className="font-medium">Yes, create a knowledge bounty</Label>
                <p className="text-gray-600 text-sm mt-1">
                  Allocate tokens to reward those who contribute high-quality knowledge
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2 p-3 border rounded-md hover:bg-gray-50">
              <RadioGroupItem value="no" id="bounty-no" className="mt-1" />
              <div>
                <Label htmlFor="bounty-no" className="font-medium">No, this is just a knowledge request</Label>
                <p className="text-gray-600 text-sm mt-1">
                  Submit the request without allocating a token reward
                </p>
              </div>
            </div>
          </RadioGroup>

          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => navigate("/call-for-knowledge/functional-area")}
              className="border-gray-300"
            >
              Back
            </Button>
            
            <Button 
              onClick={handleContinue}
              disabled={!isBounty}
              className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CallForKnowledgeBounty;
